// @ts-strict-ignore
import differenceBy from 'lodash/differenceBy';

import {Timezone} from 'common/constants';
import {CountType, FunnelSegment} from 'common/report/funnels/models/types';
import {ConversionStepBarType, NormalConversionStep} from 'common/charts/visualization/conversion-column-viz/util';
import {SegmentItem} from 'common/charts/contexts/segment-filter-controller';
import {SavedLegend} from 'common/report/constants';
import {FunnelTableRow} from 'common/report/insights/charts/funnels-step-chart/types';
import {OVERALL_PROP, OVERALL_PROP_NAME} from 'common/report/funnels/models/constants';
import {CalendarDateRange} from 'common/widgets/date-range-picker/models/types';
import {createBlankSteps} from 'common/report/funnels/util';

export const OVERALL_CONTROL_GROUP = [{prop: OVERALL_PROP, propName: OVERALL_PROP_NAME}];

export interface FunnelsStepProjectDetails {
  projectId: string;
  projectAsOfDate: null | string;
  projectTimeZone: Timezone;
  utcOffset?: number;
}

export interface StepChartData {
  conversionSteps: Array<NormalConversionStep<never, string>>;
  legendSegmentItems: SegmentItem[];
  detailedLegendSegmentItems: SegmentItem[];
  legendSegmentLabel: string;
  savedLegend: SavedLegend[];
  mainDateRangeLabel: string;
  comparisonDateRangeLabel: null | string;
  comparisonDateRange: null | CalendarDateRange;
  countType: CountType;
  funnelSegmentsForPopover: FunnelSegment[];
  funnelSegments: FunnelSegment[];
  funnelCompareSegments: null | FunnelSegment[];
  funnelLiftSegments: FunnelSegment[] | null;
  funnelMetricLabel?: string;
}

export function getBreakdownSegmentProp(
  conversionStep: NormalConversionStep<never, string>,
  stepBarIndex: number,
): string {
  return conversionStep.bars[stepBarIndex].extras;
}

export function getMainBarIndex(
  conversionStep: NormalConversionStep<never, string>,
  breakdownSegmentProp: string,
): number {
  return conversionStep.bars.findIndex((stepBar, barIndex) => {
    const matchesIndex = getBreakdownSegmentProp(conversionStep, barIndex) === breakdownSegmentProp;
    const isMain = stepBar.type !== ConversionStepBarType.Comparison;
    return matchesIndex && isMain;
  });
}

export function getComparisonBarIndex(
  conversionStep: NormalConversionStep<never, string>,
  breakdownSegmentProp: string,
): number {
  return conversionStep.bars.findIndex((stepBar, barIndex) => {
    const matchesIndex = getBreakdownSegmentProp(conversionStep, barIndex) === breakdownSegmentProp;
    const isComparison = stepBar.type === ConversionStepBarType.Comparison;
    return matchesIndex && isComparison;
  });
}

/**
 * Removes segments from 'comparisonSegments' that don't exist in 'segments', sorts in the same order as 'segments',
 * and adds missing (blank) segments to 'comparisonSegments'.
 */
export function reconcileComparisonSegments({
  segments,
  comparisonSegments,
  updatePropName = false,
}: {
  segments: FunnelSegment[];
  comparisonSegments: FunnelSegment[];
  updatePropName?: boolean;
}): FunnelSegment[] {
  const propToIndexMap = new Map(segments.map((segment, index) => [segment.prop, index]));

  // Remove segments that don't exist in 'segments'
  comparisonSegments = comparisonSegments.filter((segment) => propToIndexMap.get(segment.prop) !== undefined);

  // Match order of 'segments'
  comparisonSegments.sort((a, b) => propToIndexMap.get(a.prop) - propToIndexMap.get(b.prop));

  // Add blank segments
  const missingSegments = differenceBy(segments, comparisonSegments, `prop`);
  const blankMissingSegmentsWithIndex: Array<[FunnelSegment, number]> = missingSegments.map((segment) => [
    {...segment, ...{steps: createBlankSteps(segment.steps)}},
    segments.indexOf(segment),
  ]);
  blankMissingSegmentsWithIndex.forEach(([segment, index]) => comparisonSegments.splice(index, 0, segment));

  if (updatePropName) {
    comparisonSegments = comparisonSegments.map((segment) => ({...segment, propName: `${segment.propName} (past)`}));
  }

  return comparisonSegments;
}

export function getLegendItemsWithDataTableRows({
  rows,
  comparisonRows,
  legendSegmentItems,
}: {
  rows: FunnelTableRow[];
  comparisonRows?: FunnelTableRow[];
  legendSegmentItems: SegmentItem[];
}) {
  const tableRowLookup = new Map<string, FunnelTableRow>(rows.map((row) => [row.identifier, row]));
  const tableComparisonRowLookup = new Map<string, FunnelTableRow>(comparisonRows?.map((row) => [row.identifier, row]));

  return legendSegmentItems
    .filter((legendItem) => tableRowLookup.has(legendItem.id))
    .map((legendItem): SegmentItem => {
      return {
        ...legendItem,
        tableRow: tableRowLookup.get(legendItem.id),
        comparisonTableRow: tableComparisonRowLookup.get(legendItem.id),
      };
    });
}

export function getGroupLabel(dataGroup, {lowerCase = false} = {}) {
  const label = dataGroup ? `${dataGroup.displayName}(s)` : `Users`;
  return lowerCase ? label.toLowerCase() : label;
}
